import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '../../app/authBaseQuery';

/**
 * POST /admin/investors — "Onboard Client". A different flow entirely from
 * both KYC review and self-service registration: it creates an already-
 * active, already-verified investor in one call (KYC auto-approved), with
 * an optional one-time plaintext password when send_credentials is false.
 *
 * The response's top-level `user` is a narrower UserSummary shape than the
 * full User schema used elsewhere in the app (features/auth/normalizeUser)
 * — no account_status/email_verified_at/last_login_at/suspended_at, and
 * uuid/role (singular key) instead of id/roles. Deliberately NOT reusing
 * normalizeUser here — mapped separately so a missing field reads as
 * "never sent by this endpoint," not as a bug in the shared normalizer.
 */
function normalizeUserSummary(raw) {
  if (!raw) return null;
  return {
    id: raw.uuid ?? raw.id,
    first_name: raw.first_name ?? '',
    last_name: raw.last_name ?? '',
    full_name: [raw.first_name, raw.last_name].filter(Boolean).join(' ') || raw.email,
    email: raw.email ?? null,
    phone: raw.phone ?? null,
    roles: raw.roles ?? raw.role ?? [],
    must_change_password: !!raw.must_change_password,
    created_at: raw.created_at ?? null,
    updated_at: raw.updated_at ?? null,
  };
}

function normalizeOnboardDocument(raw) {
  if (!raw) return raw;
  return {
    id: raw.uuid ?? raw.id,
    original_name: raw.original_name ?? 'document',
    mime_type: raw.mime_type ?? null,
    size: Number(raw.size ?? 0),
    download_url: raw.download_url ?? null,
    created_at: raw.created_at ?? null,
  };
}

// The docs' own example shows submission.status: "pending", but the prose
// explicitly says onboarding never actually leaves a submission pending —
// it's auto-approved as part of the same call. Render whatever the
// response actually contains rather than assuming either value.
function normalizeOnboardSubmission(raw) {
  if (!raw) return raw;
  return {
    id: raw.uuid ?? raw.id,
    documentType: raw.document_type ?? null,
    status: raw.status ?? null,
    rejectionReason: raw.rejection_reason ?? null,
    reviewer: normalizeUserSummary(raw.reviewer),
    reviewedAt: raw.reviewed_at ?? null,
    documents: (raw.documents ?? []).map(normalizeOnboardDocument),
    createdAt: raw.created_at ?? null,
    updatedAt: raw.updated_at ?? null,
  };
}

// The KYC list endpoint (GET /admin/kyc) turned out to wrap its payload in
// a Laravel paginator under `data` despite the flat Swagger example — this
// endpoint's example was flat too (user/submission/temporary_password at
// the top level, not nested under `data`), which is plausible for a
// single-create action rather than an index/list route, but hasn't been
// independently confirmed against a live response the way the KYC list was.
// Checking both shapes defensively rather than assuming the flat one is
// definitely right, and logging loudly if neither matches so this doesn't
// silently misparse the way the KYC list did.
function unwrapOnboardResponse(response) {
  if (response && ('user' in response || 'submission' in response || 'temporary_password' in response)) {
    return response;
  }
  if (response?.data && ('user' in response.data || 'submission' in response.data)) {
    return response.data;
  }
  // eslint-disable-next-line no-console
  console.error('[onboardInvestorApi] Unexpected POST /admin/investors response shape. Got:', response);
  return {};
}

export const onboardInvestorApi = createApi({
  reducerPath: 'onboardInvestorApi',
  baseQuery: authBaseQuery(),
  endpoints: (builder) => ({
    onboardInvestor: builder.mutation({
      query: (formData) => ({ url: '/admin/investors', method: 'POST', data: formData }),
      transformResponse: (response) => {
        const body = unwrapOnboardResponse(response);
        return {
          user: normalizeUserSummary(body.user),
          submission: normalizeOnboardSubmission(body.submission),
          // Only non-null when the caller sent send_credentials: false — a
          // one-time plaintext password. Treat as sensitive/ephemeral: the
          // page that consumes this shows it once and never persists it
          // (see pages/admin/OnboardClient.jsx).
          temporaryPassword: body.temporary_password ?? null,
        };
      },
    }),
  }),
});

export const { useOnboardInvestorMutation } = onboardInvestorApi;
